import React from "react";
import { Psalm } from "../../chants/Psalm";
import { Padding } from "../../shared-components/Padding";
import { TweakProvider } from "../../utils/TweakProvider";
import { LegacyTextBlock } from "../pray/LegacyTextBlock";
import { Spacer } from "../pray/Spacer";
import { TextLine } from "../pray/TextLine";

export const GuidePage: React.FC<{}> = () => {
    return (
        <>
            <Padding>
                <div
                    style={{
                        fontWeight: "bold",
                        letterSpacing: 2,
                        textTransform: "uppercase",
                        fontSize: "140%",
                        paddingBottom: 4,
                        paddingTop: 32,
                        textAlign: "center",
                    }}
                >
                    INTRODUCTION
                </div>
            </Padding>
            <Spacer big />
            <LegacyTextBlock>
                <div
                    style={{
                        whiteSpace: "pre-wrap",
                        textAlign: "justify",
                        fontStyle: "italic",
                    }}
                >
                    {[
                        [
                            "Praise the LORD, for it is a good thing to sing prais_es un_to our God;",
                            "yea, a joy_ful and plea_sant thing it is to be thank_ful.",
                        ],
                    ].map((line) => {
                        return (
                            <>
                                <TextLine>{line[0].replace(/_/g, "")}</TextLine>
                                <TextLine indent={1}>
                                    {line[1].replace(/_/g, "")}
                                </TextLine>
                            </>
                        );
                    })}
                </div>
                <div style={{ textAlign: "right" }}>— Psalm 147:1</div>
            </LegacyTextBlock>
            <Spacer />
            <LegacyTextBlock>
                <div
                    style={{
                        whiteSpace: "pre-wrap",
                        textAlign: "justify",
                        fontStyle: "italic",
                    }}
                >
                    {[
                        [
                            "Show your_selves joy_ful un_to the LORD, all ye lands;",
                            "sing, re_joice, and give thanks.",
                        ],
                    ].map((line) => {
                        return (
                            <>
                                <TextLine>{line[0].replace(/_/g, "")}</TextLine>
                                <TextLine indent={1}>
                                    {line[1].replace(/_/g, "")}
                                </TextLine>
                            </>
                        );
                    })}
                </div>
                <div style={{ textAlign: "right" }}>— Psalm 98:5</div>
            </LegacyTextBlock>
            <Spacer />
            <LegacyTextBlock>
                <div
                    style={{
                        whiteSpace: "pre-wrap",
                        textAlign: "justify",
                        fontStyle: "italic",
                    }}
                >
                    {[
                        [
                            "Se_ven times a day do I praise thee;",
                            "be_cause of thy right_eous judg_ments.",
                        ],
                    ].map((line) => {
                        return (
                            <>
                                <TextLine>{line[0].replace(/_/g, "")}</TextLine>
                                <TextLine indent={1}>
                                    {line[1].replace(/_/g, "")}
                                </TextLine>
                            </>
                        );
                    })}
                </div>
                <div style={{ textAlign: "right" }}>— Psalm 119:164</div>
            </LegacyTextBlock>
            <Spacer />
            <LegacyTextBlock>
                For thousands of years, the People of God have sung the Psalms
                every day as an offering of thanks and praise to Almighty God,
                as an act of love, and as a response to his direct command and
                provision for us to do so.
            </LegacyTextBlock>
            <LegacyTextBlock>
                As{" "}
                <a
                    style={{ textDecoration: "underline" }}
                    href="https://theopolisinstitute.com/the-psalter-should-be-woven-into-the-fabric-of-worship/"
                    target="_blank"
                >
                    James Jordan writes
                </a>
                ,
            </LegacyTextBlock>
            <LegacyTextBlock>
                <div style={{ paddingLeft: 24, paddingRight: 24 }}>
                    God likes psalms. He wrote them, and He likes to hear them
                    sung. If we love Him, we will make the effort to learn them,
                    all of them, and sing them to Him before His throne on His
                    day.
                </div>
            </LegacyTextBlock>
            <LegacyTextBlock>
                The exact implementation of this practice has varied with time
                and place. From early in the history of the Church, by the time
                of Saint Benedict in the 5th century, the practice of praying
                seven times during the day (Psalm 119:164) and once at night
                (Psalm 119:62) was observed. The whole Psalter could be sung
                every week within this framework.
            </LegacyTextBlock>
            <LegacyTextBlock>
                Over the centuries, the participation of laypeople in this
                'Liturgy of the Hours' has varied and has generally been less
                than that of monastics. By the time of the English Reformation,
                it was common for laypeople to attend Morning Prayer and Evening
                Prayer (and Mass) on feast days — services which were conducted
                in Latin — but otherwise to leave the Liturgy of the Hours to
                the monks. Thomas Cranmer, desiring that laypeople be exposed to
                the Scriptures in full and in English, condensed the eight
                Offices into two larger services with substantial readings in
                English covering most of the Bible every year and the whole
                Psalter every month.
            </LegacyTextBlock>
            <LegacyTextBlock>
                These Anglican Daily Offices have continued to this day,
                especially in the form of Choral Evensong, where the Psalms and
                Canticles are sung by a choir to beautiful settings, while the
                congregation listens in silence. Private recitation of the
                Offices among laypeople is somewhat common but is often said
                rather than sung, and so still falls short of the Scriptural
                command and provision to "sing to the Lord!" — rather like
                reading out the contents of a hymn book. No doubt there are many
                reasons for this, but one is the enormous difficulty of
                assembling and learning to use a suitable set of resources for
                singing the Daily Office, especially for those without a
                significant musical background. SingTheOffice.com is intended as
                a remedy to this difficulty.
            </LegacyTextBlock>
            <LegacyTextBlock>
                On SingTheOffice.com, the Psalms are provided set out in the
                'square notation' commonly used by monks. This notation was
                designed with practicality in mind, and is generally considered
                easier than modern musical notation for people without a musical
                background. The settings provided here are plainsong, a form of
                music originating at least fifteen centuries ago and used to
                sing the Psalms ever since. With a bit of practice, anyone can
                learn to sing the Psalms in plainsong, and participate daily in
                this beautiful form of worship.
            </LegacyTextBlock>
            <LegacyTextBlock>
                The best way to be introduced to plainsong is by example, so
                Psalm 1 is given below, and can be used alongside this{" "}
                <a
                    style={{ textDecoration: "underline" }}
                    href="https://youtu.be/uakqB3is63E?t=48"
                >
                    recording of Psalm 1 being sung
                </a>
                .
            </LegacyTextBlock>
            <TweakProvider initialState={{}}>
                <Psalm
                    psalmRef={{ type: "full", number: 1 }}
                    excludeGloriaPatri
                />
            </TweakProvider>
            <LegacyTextBlock>
                More plainsong Psalm recordings (not all settings being
                identical to those on this site) can be found{" "}
                <a
                    style={{ textDecoration: "underline" }}
                    href="https://canticasacra.org/?page_id=8623"
                >
                    here
                </a>
                .
            </LegacyTextBlock>
            <LegacyTextBlock>
                In plainsong, the text is sung smoothly at about the same pace
                that it would be said, without forcing the text into a
                particular rhythm. A good breath should be taken between each
                half-line (where there are vertical bars in the notation). The
                tone can be sung at any pitch that you find comfortable: when
                you come to a Psalm on this site, clicking{" "}
                <strong>More...</strong> in the top right will allow you to play
                the notes of the tone in order to learn how to sing it, and the
                pitch can be changed with the <strong>Higher</strong> and{" "}
                <strong>Lower</strong> buttons.
            </LegacyTextBlock>
            <LegacyTextBlock>
                On SingTheOffice.com, the whole liturgy for Mattins and Evensong
                (and for Compline, which has returned to common Anglican
                practice more recently) is given with all the musical settings
                right in the order of service. The instructions are given for
                how to celebrate the Daily Office as a service with multiple
                people in attendance, but if praying alone, one acts as both the
                Officiant and the 'People'.
            </LegacyTextBlock>
            <LegacyTextBlock>
                The most important parts of the Daily Office to sing are the
                Psalms and Canticles, as they are expressly given as songs in
                the Bible; however, it is common practice to sing much more of
                the liturgy of the Daily Office. On SingTheOffice.com, just
                about everything that could be sung is given with a musical
                setting, but note that it is acceptable to say things that you
                are not confident to sing. Learning to sing the Daily Office is
                a marathon, not a sprint! It's also worth bearing in mind that
                it's more important here to sing <i>joyfully</i> than to sing{" "}
                <i>accurately</i>.
            </LegacyTextBlock>
            <Spacer big />
            <LegacyTextBlock>
                While not exactly identical to any of the following, this site
                is indebted to the Proctor and Frere Manual of Plainsong, the St
                Dunstan's Plainsong Psalter, the Palmer Sarum Psalter, the
                McMaster Sarum Psalter and Hymnal, the English Hymnal, the 1662,
                1928, and 2019 Books of Common Prayer, to the ancestor works
                that fed into these, and ultimately to Almighty God for his
                loving provision of the Psalms and Canticles of Holy Scripture,
                and for his providence in guiding the development of plainsong
                and the Daily Offices throughout history. The work contained in
                this site is offered freely, to his glory, with the hope that it
                enables more individuals so inclined to sing his praises on a
                daily basis.
            </LegacyTextBlock>
            <div style={{ paddingTop: 24, textAlign: "center" }}>
                <a style={{ textDecoration: "underline" }} href="/">
                    Return to Home
                </a>
            </div>
            <Spacer big />
        </>
    );
};
